// Pure CPU reduce() — mirrors pypic.reductions.reduce. Typed arrays in,
// typed arrays out. No DOM, no THREE, no store. Future home:
// `@webpic/reductions/src/reduce.ts`.
//
// Flat-index convention: `idx = i * (ny * nz) + j * nz + k` (matches
// physics/grid.ts:121-139 and the rest of the magviz physics layer).
//
// NaN policy: 'omit' (pypic default). NaN cells are skipped from the
// accumulator AND from the count, so `mean` collapses to the
// finite-cell mean and `max`/`min` ignore NaN. Cells outside the domain
// mask are also skipped. Returns `{ kind: 'empty' }` when zero cells
// matched, distinct from a NaN poisoned result.

import type { ResolvedDomain } from '@store/selections';
import {
    AXIS_LABELS,
    isSupported,
    type AxisLabel,
    type ReductionResult,
    type ReductionSpec,
    type SupportedOp,
} from './types';

export interface ReduceGrid {
    readonly nx: number;
    readonly ny: number;
    readonly nz: number;
    /** Lower corner in physical units. */
    readonly origin: readonly [number, number, number];
    /** Cell width per axis. Equal-spacing only (magviz datasets are uniform Cartesian). */
    readonly spacing: readonly [number, number, number];
}

type AxisSet = readonly [boolean, boolean, boolean];

function axisSet(axes: ReadonlyArray<AxisLabel>): AxisSet {
    return [axes.includes('x'), axes.includes('y'), axes.includes('z')];
}

function survivingAxes(reduced: AxisSet): AxisLabel[] {
    const out: AxisLabel[] = [];
    for (let a = 0; a < 3; a++) {
        if (!reduced[a]) out.push(AXIS_LABELS[a]!);
    }
    return out;
}

/**
 * Reduce a single 3-D field array along `spec.axes`. Returns a discriminated
 * `ReductionResult` (scalar / profile / unsupported / empty). All caller
 * coupling lives in the result discriminator — no thrown errors for the
 * supported-but-empty case.
 */
export function reduce(
    field: Float32Array,
    grid: ReduceGrid,
    spec: ReductionSpec,
    domain: ResolvedDomain,
): ReductionResult {
    if (!isSupported(spec.op)) {
        return { kind: 'unsupported', op: spec.op, reason: `op '${spec.op}' is reserved for v2` };
    }
    const op = spec.op;
    const reduced = axisSet(spec.axes);
    const survivors = survivingAxes(reduced);

    if (survivors.length === 0) {
        return reduceScalar(field, grid, op, domain);
    }
    if (survivors.length === 1) {
        return reduceProfile(field, grid, op, domain, survivors[0]!);
    }
    // 2 surviving axes → 2-D heatmap, deferred.
    return {
        kind: 'unsupported',
        op,
        reason: '2-D heatmap output coming in v2',
    };
}

// ─── scalar (0-D) ──────────────────────────────────────────────────────────

function reduceScalar(
    field: Float32Array,
    grid: ReduceGrid,
    op: SupportedOp,
    domain: ResolvedDomain,
): ReductionResult {
    const { nx, ny, nz, origin, spacing } = grid;
    let sum = 0;
    let count = 0;
    let mx = -Infinity;
    let mn = Infinity;
    for (let i = 0; i < nx; i++) {
        const px = origin[0] + (i + 0.5) * spacing[0];
        for (let j = 0; j < ny; j++) {
            const py = origin[1] + (j + 0.5) * spacing[1];
            for (let k = 0; k < nz; k++) {
                const pz = origin[2] + (k + 0.5) * spacing[2];
                if (cellMasked(px, py, pz, domain)) continue;
                const v = field[i * (ny * nz) + j * nz + k]!;
                if (!Number.isFinite(v)) continue;
                count++;
                if (op === 'sum' || op === 'mean') sum += v;
                if (op === 'max' && v > mx) mx = v;
                if (op === 'min' && v < mn) mn = v;
            }
        }
    }
    if (count === 0) return { kind: 'empty' };
    const value =
        op === 'sum' ? sum :
        op === 'mean' ? sum / count :
        op === 'max' ? mx :
        mn;
    return { kind: 'scalar', op, value, count };
}

// ─── profile (1-D) ─────────────────────────────────────────────────────────

function reduceProfile(
    field: Float32Array,
    grid: ReduceGrid,
    op: SupportedOp,
    domain: ResolvedDomain,
    survivor: AxisLabel,
): ReductionResult {
    const { nx, ny, nz, origin, spacing } = grid;
    // Survivor dimensions
    const surviveDim = survivor === 'x' ? nx : survivor === 'y' ? ny : nz;
    const sumBuf = new Float64Array(surviveDim);
    const countBuf = new Int32Array(surviveDim);
    const initial = op === 'max' ? -Infinity : op === 'min' ? Infinity : 0;
    const extBuf = op === 'max' || op === 'min' ? new Float64Array(surviveDim) : null;
    if (extBuf) extBuf.fill(initial);

    for (let i = 0; i < nx; i++) {
        const px = origin[0] + (i + 0.5) * spacing[0];
        for (let j = 0; j < ny; j++) {
            const py = origin[1] + (j + 0.5) * spacing[1];
            for (let k = 0; k < nz; k++) {
                const pz = origin[2] + (k + 0.5) * spacing[2];
                if (cellMasked(px, py, pz, domain)) continue;
                const v = field[i * (ny * nz) + j * nz + k]!;
                if (!Number.isFinite(v)) continue;
                const s = survivor === 'x' ? i : survivor === 'y' ? j : k;
                countBuf[s]!++;
                if (extBuf) {
                    if (op === 'max') {
                        if (v > extBuf[s]!) extBuf[s] = v;
                    } else {
                        if (v < extBuf[s]!) extBuf[s] = v;
                    }
                } else {
                    sumBuf[s]! += v;
                }
            }
        }
    }

    let total = 0;
    for (let s = 0; s < surviveDim; s++) total += countBuf[s]!;
    if (total === 0) return { kind: 'empty' };

    const values = new Float32Array(surviveDim);
    const coords = new Float32Array(surviveDim);
    const survAxis = survivor === 'x' ? 0 : survivor === 'y' ? 1 : 2;
    const o = origin[survAxis];
    const dx = spacing[survAxis];
    for (let s = 0; s < surviveDim; s++) {
        coords[s] = o + (s + 0.5) * dx;
        const c = countBuf[s]!;
        if (c === 0) {
            values[s] = NaN;
            continue;
        }
        if (op === 'sum') values[s] = sumBuf[s]!;
        else if (op === 'mean') values[s] = sumBuf[s]! / c;
        else values[s] = extBuf![s]!;
    }
    return { kind: 'profile', op, axis: survivor, coords, values, counts: countBuf };
}

// ─── masking ───────────────────────────────────────────────────────────────

function cellMasked(x: number, y: number, z: number, domain: ResolvedDomain): boolean {
    if (domain.kind === 'full') return false;
    if (domain.kind === 'box') {
        return (
            x < domain.lower[0] || x > domain.upper[0] ||
            y < domain.lower[1] || y > domain.upper[1] ||
            z < domain.lower[2] || z > domain.upper[2]
        );
    }
    const dx = x - domain.center[0];
    const dy = y - domain.center[1];
    const dz = z - domain.center[2];
    const inside = (dx * dx + dy * dy + dz * dz) <= domain.radius * domain.radius;
    return domain.keep === 'inside' ? !inside : inside;
}
